import React, { useEffect, useState } from 'react';
import { WarningAmber } from '@mui/icons-material';
import { Box, Button, Chip, Slide, Stack, Typography } from '@mui/material';

import { useFeatureToggle } from '../hooks/useFeatureToggle';
import type { FeatureToggleConfig } from '../types';

interface FeatureToggleBannerProps {
  /** Configuration for the feature toggle */
  config: FeatureToggleConfig;
  /** Custom sx prop for styling */
  sx?: object;
  /** Optional custom handler for switching to classic (for feature-specific logic like resetting counters) */
  onSwitchToClassic?: () => void;
  /** Optional custom handler for switching to new */
  onSwitchToNew?: () => void;
}

/**
 * Banner component for feature toggle
 *
 * Sticky footer banner that stays at the bottom of the viewport.
 * Always visible when gate is enabled, shows dynamic message based on current version.
 * Animates up on mount (after 1s delay) and down on unmount.
 */
export function FeatureToggleBanner({
  config,
  sx,
  onSwitchToClassic,
  onSwitchToNew,
}: FeatureToggleBannerProps) {
  const { showBanner, isOnNew, switchToNew, switchToClassic, sunsetInfo } = useFeatureToggle(config);

  // Use custom handlers if provided, otherwise use default from hook
  const handleSwitchToNew = onSwitchToNew ?? switchToNew;
  const handleSwitchToClassic = onSwitchToClassic ?? switchToClassic;
  const [isVisible, setIsVisible] = useState(false);

  // Delay showing banner by 1 second after mount
  useEffect(() => {
    if (showBanner) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [showBanner]);

  if (!showBanner) return null;

  // Default banner text
  const messageOnNew =
    config.bannerText?.messageOnNew ?? "You're using the new Recording experience";
  const messageOnClassic =
    config.bannerText?.messageOnClassic ?? 'Try the new Recording experience';
  const switchToNewLabel = config.bannerText?.switchToNewLabel ?? 'Switch to new';
  const switchToClassicLabel = config.bannerText?.switchToClassicLabel ?? 'Stay on classic';

  const currentMessage = isOnNew ? messageOnNew : messageOnClassic;

  return (
    <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          boxShadow: 2,
          zIndex: 1000,
          ...sx,
        }}
      >
        <Box
          sx={{
            maxWidth: {
              xs: '100%',
              md: 'calc(1400px + 64px)',
            },
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 4 },
            py: 2,
          }}
        >
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "flex-end" }}>
            <Typography variant="body2" color="text.primary">
              {currentMessage}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              {isOnNew ? (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleSwitchToClassic}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                  }}
                >
                  {switchToClassicLabel}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSwitchToNew}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                    }}
                  >
                    {switchToNewLabel}
                  </Button>
                  {sunsetInfo && !sunsetInfo.isPastDue && (
                    <Chip
                      icon={<WarningAmber />}
                      label={sunsetInfo.label}
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderColor: 'warning.main',
                        color: 'warning.dark',
                        px: 1,
                        '& .MuiChip-icon': {
                          color: 'warning.main',
                        },
                      }}
                    />
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Slide>
  );
}
